import { v4 as uuidv4 } from 'uuid';

import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { getStructureIndexByElementUuid } from 'editor/src/store/design/selector/getStructureIndexByElementUuid';
import { updateMediaElementAction } from 'editor/src/store/design/slice';
import { conditionGroupChildenKey, DesignData, MediaText, Spread, StructureIndex } from 'editor/src/store/design/types';
import { getSpreadQrSourceTextUuids } from 'editor/src/store/design/util/qrSourceTexts';
import { sortInReadingOrder } from 'editor/src/store/design/util/textFlow';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import { canHaveFixedTextFrame } from 'editor/src/store/editorModules/warnings/utils/getTextOverflowWarning';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

export interface LinkableTextFrame {
  element: MediaText;
  address: StructureIndex;
}

/**
 * Whether a text can be a frame of a chain: plain wrapping text, which a fixed frame needs, in a font
 * the editor lays out itself, whose text nothing else writes and which nothing else hides. A protected
 * uploaded font never loads here (the server draws it); a shared field, a code-set column or condition
 * variants put a whole value into the one frame, where the flow holds only a slice of the chain; and
 * text poured into a hidden frame is lost from the canvas and the print.
 */
export function canJoinTextFlow(state: RootState, element: MediaText, spreadIndex: number) {
  const { sharedFieldId, variableColumn, textVariants, fontFamily } = element.extra;
  const { designData } = state.design;
  const spread = designData?.spreads[spreadIndex];
  return (
    canHaveFixedTextFrame(element) &&
    !sharedFieldId &&
    !variableColumn &&
    !textVariants &&
    !element.slotFrame &&
    !(spread && getHideableTextUuids(designData, spread).has(element.uuid)) &&
    !state.fonts.uploadedFonts.some(({ fontFile, isProtected }) => isProtected && fontFile === fontFamily)
  );
}

// the layers of a condition option, the texts of a reflow row (an optional one collapses when empty)
// and the texts an artwork-only QR reads; the layers of a product layout's frames carry a slotFrame
function getHideableTextUuids(designData: DesignData | undefined, spread: Spread) {
  const uuids = getSpreadQrSourceTextUuids(designData, spread);
  Object.entries(spread.conditionGroup?.children ?? {}).forEach(([key, children]) => {
    if (key !== conditionGroupChildenKey) {
      children.forEach((child) => child.type !== 'condition' && uuids.add(child.id));
    }
  });
  spread.reflowGroups?.forEach(({ rows }) =>
    rows.forEach(({ elementUuids }) => elementUuids.forEach((uuid) => uuids.add(uuid))),
  );
  return uuids;
}

/**
 * The selected texts that can be linked (canJoinTextFlow), in reading order (sortInReadingOrder).
 * Only frames on one spread, and only frames not linked yet: linking a chain again would join its
 * texts with paragraph breaks where its cuts were.
 */
export function getLinkableTextFrames(state: RootState): LinkableTextFrame[] {
  const { designData } = state.design;
  const frames: LinkableTextFrame[] = [];
  state.editor.selectedElementUuids.forEach((uuid) => {
    const address = getStructureIndexByElementUuid(designData, uuid);
    const element =
      address && designData?.spreads[address.spreadIndex].pages[address.pageIndex].groups.media?.[address.elementIndex];
    if (
      address &&
      element?.type === 'text' &&
      canJoinTextFlow(state, element, address.spreadIndex) &&
      !element.extra.textFlow
    ) {
      frames.push({ element, address });
    }
  });
  if (new Set(frames.map(({ address }) => address.spreadIndex)).size > 1) {
    return [];
  }
  return sortInReadingOrder(frames, ({ element }) => element);
}

/**
 * Links the selected text frames into one chain. Their texts join into the first frame
 * (a blank line between them), and so does a host's field key; every frame becomes a fixed frame, and the flow (useTextFlowWatcher)
 * pours the text over them in the same undo step.
 */
const linkTextFramesOperation = () => (dispatch: ThunkDispatch, getState: () => RootState) => {
  const frames = getLinkableTextFrames(getState());
  if (frames.length < 2) {
    return;
  }
  const id = uuidv4();
  const text = frames
    .map(({ element }) => element.extra.text.trim())
    .filter(Boolean)
    .join('\n\n');
  // a host fills the chain through one key, its first frame's (or the first key a frame carries)
  const fieldKey = frames.find(({ element }) => element.extra.fieldKey)?.element.extra.fieldKey;

  batch(() => {
    dispatch(saveUndoRedoStateOperation('Text frames linked'));
    frames.forEach(({ element, address }, index) => {
      dispatch(
        updateMediaElementAction({
          elementAddress: address,
          elementUpdate: {
            extra: {
              ...element.extra,
              text: index === 0 ? text : '',
              fieldKey: index === 0 ? fieldKey : undefined,
              fixedFrame: true,
              textFlow: { id, index },
            },
          },
        }),
      );
    });
  });
};

export default linkTextFramesOperation;
